import { Component, computed, inject, signal } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { RouterModule } from '@angular/router';
import {
  ArtStandort,
  DEFAULT_STANDORT_INPUT,
  LehDistanz,
  Parken,
  Sichtbarkeit,
  StandortInput,
  StrasseTyp,
  evaluateStandort,
  formatEuro,
  formatMonths,
} from '../core/standort.model';

interface Option<T> {
  val: T;
  label: string;
}

/** Lokalisierte Routen-Segmente – identisch mit app.routes.ts. */
const ROUTE_RECHNER = $localize`:@@routes.rechner:rechner`;
const ROUTE_AMORTISATION = $localize`:@@routes.rechner.item.amortisation:amortisation`;

/**
 * Interaktiver Standort-Score-Rechner.
 *
 * Reines UI: die gesamte Rechnung steckt in `core/standort.model.ts`. Wer nichts
 * anfasst, sieht nach dem Klick trotzdem eine echte Zahl (Durchschnittsstandort).
 */
@Component({
  selector: 'app-standort-rechner',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './standort-rechner.component.html',
  styleUrl: './standort-rechner.component.css',
})
export class StandortRechnerComponent {
  private readonly document = inject(DOCUMENT);

  readonly input = signal<StandortInput>({ ...DEFAULT_STANDORT_INPUT });
  readonly evaluated = signal(false);

  readonly result = computed(() => evaluateStandort(this.input()));

  /** RouterLink zum Amortisationsrechner mit vorbelegtem Umsatz. */
  readonly amortLink = ['/', ROUTE_RECHNER, ROUTE_AMORTISATION];
  readonly amortQueryParams = computed(() => ({ umsatz: Math.round(this.result().revenue.mid) }));

  /** Farbe des Gauges je Stufe – Gelb / Petrol / Terrakotta (CI). */
  readonly gaugeColor = computed(() => {
    switch (this.result().stufe.key) {
      case 'sehr_gut':
        return 'var(--yellow, #f5b31d)';
      case 'solide':
        return 'var(--teal, #16847a)';
      default:
        return 'var(--clay, #c76a3c)';
    }
  });

  // --- Optionen für die Auswahl-Gruppen ---
  readonly artOptions: Option<ArtStandort>[] = [
    { val: 'hof', label: $localize`:@@standort.opt.art.hof:Hofeinfahrt / eigener Betrieb` },
    { val: 'wohn', label: $localize`:@@standort.opt.art.wohn:Wohngebiet / Ortsmitte` },
    { val: 'tank', label: $localize`:@@standort.opt.art.tank:Tankstelle / Rastplatz` },
    { val: 'camping', label: $localize`:@@standort.opt.art.camping:Campingplatz / Tourismus` },
    { val: 'gewerbe', label: $localize`:@@standort.opt.art.gewerbe:Gewerbegebiet` },
  ];
  readonly strasseOptions: Option<StrasseTyp>[] = [
    { val: 'anlieger', label: $localize`:@@standort.opt.strasse.anlieger:Ruhige Anliegerstraße` },
    { val: 'ortsdurchfahrt', label: $localize`:@@standort.opt.strasse.ortsdurchfahrt:Ortsdurchfahrt` },
    { val: 'landstrasse', label: $localize`:@@standort.opt.strasse.landstrasse:Landstraße mit Durchgangsverkehr` },
    { val: 'hauptstrasse', label: $localize`:@@standort.opt.strasse.hauptstrasse:Hauptverkehrsstraße ohne Halt` },
  ];
  readonly sichtOptions: Option<Sichtbarkeit>[] = [
    { val: 'strasse', label: $localize`:@@standort.opt.sicht.strasse:Direkt von der Straße` },
    { val: 'hof', label: $localize`:@@standort.opt.sicht.hof:Auf dem Hof, ausgeschildert` },
    { val: 'abseits', label: $localize`:@@standort.opt.sicht.abseits:Abseits` },
  ];
  readonly parkenOptions: Option<Parken>[] = [
    { val: 'davor', label: $localize`:@@standort.opt.parken.davor:Direkt davor` },
    { val: 'wenige', label: $localize`:@@standort.opt.parken.wenige:Wenige Meter` },
    { val: 'schwierig', label: $localize`:@@standort.opt.parken.schwierig:Schwierig` },
  ];
  readonly lehOptions: Option<LehDistanz>[] = [
    { val: 'ueber5', label: $localize`:@@standort.opt.leh.ueber5:Über 5 km` },
    { val: '1bis5', label: $localize`:@@standort.opt.leh.1bis5:1 – 5 km` },
    { val: 'unter1', label: $localize`:@@standort.opt.leh.unter1:Unter 1 km` },
  ];

  // --- Aktionen ---
  select<K extends keyof StandortInput>(key: K, value: StandortInput[K]): void {
    this.input.update((i) => ({ ...i, [key]: value }));
  }

  setHaushalte(value: number | string): void {
    this.input.update((i) => ({ ...i, haushalte: Number(value) }));
  }

  auswerten(): void {
    this.evaluated.set(true);
  }

  /** In-Page-Anker – plain href="#id" scheitert an <base href="/">. */
  scrollToAnchor(event: Event, id: string): void {
    event.preventDefault();
    this.document.getElementById(id)?.scrollIntoView({ behavior: 'auto', block: 'start' });
  }

  // --- Formatierung fürs Template ---
  euro = formatEuro;
  months = formatMonths;

  amortLabel(): string {
    const r = this.result();
    if (r.breakevenMonths === null) return $localize`:@@standort.amort.nie:trägt sich nicht`;
    return $localize`:@@standort.amort.monate:ca. ${this.months(r.breakevenMonths)}:MONATE: Monate`;
  }

  amortSub(): string {
    const r = this.result();
    if (r.breakevenMonths === null) return $localize`:@@standort.amort.nie.sub:laufende Kosten über dem Rohertrag`;
    const afa =
      r.breakevenMonths <= 60
        ? $localize`:@@standort.amort.afa.innerhalb:innerhalb der Abschreibung`
        : $localize`:@@standort.amort.afa.ueber:über der Abschreibung`;
    return $localize`:@@standort.amort.sub:≈ ${this.euro(r.monthlySurplus)}:UEBERSCHUSS:/Mon. Überschuss · ${afa}:AFA:`;
  }

  faktorLabel(): string {
    const v = this.result().vergleich;
    if (!v) return '';
    return v.faktor.toFixed(1).replace('.', ',');
  }
}
